import { Chip } from "@itk/mui-components";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";

type Props = {
  labels: string[];
};

export const ChipList = (props: Props) => {
  const { labels } = props;
  const theme = useTheme();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const chipWidths = useRef<number[]>([]);

  const [width, setWidth] = useState(0);
  const [chipsToKeep, setChipsToKeep] = useState(labels.length);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!width) return;

    chipRefs.current.forEach((el, i) => {
      if (el && el.offsetWidth > 0) {
        chipWidths.current[i] = el.offsetWidth;
      }
    });

    let total = 0;
    let count = 0;
    for (let i = 0; i < labels.length; i++) {
      const itemWidth = chipWidths.current[i] || 0;
      if (total + itemWidth < width - 100) {
        total += itemWidth;
        count++;
      } else {
        break;
      }
    }
    setChipsToKeep(count);
  }, [width, labels]);

  const chipSx = {
    bgcolor: theme.palette.customColors.lightGrey,
    ml: 1,
    borderRadius: 1,
    border: `1px solid ${theme.palette.customColors.grey2}`
  };

  return (
    <Stack gap={1} direction="row" alignItems="center" flex={1} overflow="hidden" ref={wrapperRef}>
      {labels
        .sort((a, b) => a.localeCompare(b))
        .map((label, i) => (
          <Box
            key={i}
            ref={(el: HTMLDivElement | null) => {
              chipRefs.current[i] = el;
            }}
            sx={{ display: i < chipsToKeep ? undefined : "none" }}
          >
            <Chip label={label} size="small" sx={chipSx} />
          </Box>
        ))}
      {chipsToKeep < labels.length && (
        <Box>
          <Tooltip
            title={
              <Stack sx={{ gap: 0.5 }}>
                {labels
                  .slice(chipsToKeep)
                  .sort((a, b) => a.localeCompare(b))
                  .map((label, index) => (
                    <Typography key={index} variant="caption" color="text.primary">
                      {label}
                    </Typography>
                  ))}
              </Stack>
            }
            placement="bottom-start"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  boxShadow: theme.shadows[3],
                  border: `1px solid ${theme.palette.customColors.grey2}`,
                  p: 1.5
                }
              }
            }}
          >
            <span onClick={e => e.stopPropagation()}>
              <Chip label={`+${labels.length - chipsToKeep}`} size="small" sx={{ ...chipSx, cursor: "default" }} />
            </span>
          </Tooltip>
        </Box>
      )}
    </Stack>
  );
};
